import { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './AudioWorkspace.module.css';

export interface ImportResult {
  rawData: Uint8Array;
  audioBuffer: AudioBuffer;
  peaks: Float32Array;
  duration: number;
  mimeType: string;
}

interface Props {
  onStage: (result: ImportResult, label: string) => void;
}

const ACCEPTED = '.wav,.mp3,.ogg,.m4a,.aac';
const ACCEPTED_TYPES = /\.(wav|mp3|ogg|m4a|aac)$/i;

export function ImportPanel({ onStage }: Props) {
  const { t } = useTranslation('audioWorkspace');
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const processFile = useCallback(async (file: File) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const rawData = new Uint8Array(arrayBuffer);
      const { decodeAudio, generatePeaks } = await import('@/audio/audioProcessor.ts');
      const audioBuffer = await decodeAudio(rawData);
      const peaks = generatePeaks(audioBuffer);
      const label = file.name.replace(/\.[^.]+$/, '');
      onStage({ rawData, audioBuffer, peaks, duration: audioBuffer.duration, mimeType: file.type || 'audio/wav' }, label);
    } catch (err) {
      console.error('[ImportPanel] Failed to import:', err);
    }
  }, [onStage]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    // Reset so re-selecting same file works
    e.target.value = '';
  }, [processFile]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(f =>
      (f.type.startsWith('audio/') && !f.type.includes('webm')) || ACCEPTED_TYPES.test(f.name)
    );
    if (files[0]) processFile(files[0]);
  }, [processFile]);

  return (
    <div className={styles.content}>
      <div className={styles.importCenter}>
        <label
          className={`${styles.dropZone} ${dragging ? styles.dropZoneActive : ''}`}
          data-file-drop-target=""
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
        >
          {t('dropZoneLabel')}
          <br />
          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{t('dropZoneFormats')}</span>
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPTED}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </label>
      </div>
    </div>
  );
}
